import * as React from "react";
import { Link, useLocation } from "react-router";
import { BellRing, Smartphone, X } from "lucide-react";
import { browserPushState, detectPlatform, isApple } from "@ozpanel/kit";
import { Button, useIsPhone, useStandalone } from "@ozpanel/ui";
import { openInstalledApp, useAppInstalled } from "./installPrompt";

// Prompting where it is suitable (Oz, 2026-11-03: "prompt the user to install where possible … properly
// prompt for notifications when suitable"). ONE banner, at most one message, only when it applies:
//
//   * a phone in the browser, app not installed → "Get the app on this phone";
//   * a phone in the browser whose app IS installed (Chrome on Android can tell) → "Open the app";
//   * the installed app (or Android's browser, where alerts work without installing) whose alerts have
//     never been asked for → "Turn on new-mail alerts".
//
// Both lead to Notifications, the page that does it for this device. The browser's own permission
// question is never raised from here on load: browsers allow it only from a tap, and a question nobody
// asked for is the one people answer "Block" — after which it cannot be asked again. Not shown on that
// page itself, nor once alerts are allowed or blocked, nor after "Not now" on this device.

const DISMISS_KEY = "ozpanel.setupPrompt.dismissed";

function dismissed(kind: string): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY + "." + kind) === "1";
  } catch {
    return false;
  }
}

function dismiss(kind: string): void {
  try {
    window.localStorage.setItem(DISMISS_KEY + "." + kind, "1");
  } catch {
    /* Private window or blocked storage: the banner simply comes back next time. */
  }
}

export function SetupPrompt() {
  const isPhone = useIsPhone();
  const standalone = useStandalone();
  const { pathname } = useLocation();
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);
  const platform = React.useMemo(() => detectPlatform(), []);
  const appInstalled = useAppInstalled();

  if (/\/notifications\/?$/.test(pathname)) return null;
  const push = browserPushState();
  // An iPhone shows web alerts only from the installed app; Android's browser shows them as it is.
  const alertsHere = push === "prompt" && (standalone || (!isApple(platform) && platform.os === "android"));
  // Already installed (Chrome on Android can say so): open it, rather than "install" again.
  const kind = isPhone && !standalone ? (appInstalled ? "open" : "install") : alertsHere ? "alerts" : null;
  if (!kind || dismissed(kind)) return null;

  if (kind === "open") {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-lg border border-line bg-bg2 px-3 py-2.5 text-[13.5px]" role="note">
        <Smartphone size={18} aria-hidden className="shrink-0 text-accent" />
        <span className="min-w-0 flex-1">You have the app on this phone — it opens straight to your mail.</span>
        <Button size="sm" variant="accent" onClick={() => openInstalledApp()}>
          Open the app
        </Button>
        <Button size="sm" variant="ghost" aria-label="Not now" onClick={() => { dismiss(kind); rerender(); }}>
          <X size={16} aria-hidden />
        </Button>
      </div>
    );
  }
  const install = kind === "install";
  return (
    <div className="mb-4 flex items-center gap-3 rounded-lg border border-line bg-bg2 px-3 py-2.5 text-[13.5px]" role="note">
      {install ? <Smartphone size={18} aria-hidden className="shrink-0 text-accent" /> : <BellRing size={18} aria-hidden className="shrink-0 text-accent" />}
      <span className="min-w-0 flex-1">
        {install ? "Get the app on this phone — it opens like an app and stays signed in." : "Turn on new-mail alerts on this device."}
      </span>
      <Link to="/notifications" className="shrink-0 font-medium text-accent hover:underline">
        {install ? "Install" : "Turn on"}
      </Link>
      <Button
        size="sm"
        variant="ghost"
        aria-label="Not now"
        onClick={() => {
          dismiss(kind);
          rerender();
        }}
      >
        <X size={16} aria-hidden />
      </Button>
    </div>
  );
}
